/**
 * useMealRefresh - Hook for handling meal refresh functionality
 *
 * This hook encapsulates the logic for refreshing a meal with a new recipe,
 * including undo functionality, loading states, and recipe exclusion tracking.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash.debounce';
import uuidGen from 'uuid';
import { fetchDocumentsById } from '@utils/Content';
import AuthStore from '@stores/AuthStore';
import { getConfig } from '@utils/Env';
import { isSunbasketFood } from '@utils/Sunbasket';
import { getPrimaryMeal, getParticipantsForProfileByMealType, getMealTypeInfoByName } from '@utils/Meals';
import { addDefaultAddSwapFilters, addDefaultAddSwapTags } from '@utils/Nutrition';
import Analytics from '@utils/Analytics';

/**
 * Custom hook for meal refresh functionality
 *
 * @param {Object} config - Configuration object
 * @param {Array} config.meals - Current meals array
 * @param {Object} config.recipes - Recipes store data
 * @param {Object} config.foods - Foods store data
 * @param {Function} config.addMeals - Function to add new meals
 * @param {Function} config.removeMeals - Function to remove meals
 * @param {Function} config.batchOperations - Function to perform atomic meal operations
 * @param {Function} config.editMealBatches - Function to edit meal batches
 * @param {Function} config.getDefaultAddSwapSettings - Function to get default settings
 * @param {Object} config.profile - User profile data
 * @param {Object} config.user - User data
 * @param {string} config.mealType - Meal type (breakfast, lunch, dinner)
 * @param {number} config.offset - Meal offset
 * @param {Function} config.startReplaceMeal - Function to open replace modal
 * @returns {Object} Refresh functionality and state
 */
export function useMealRefresh({
    meals,
    recipes,
    foods,
    addMeals,
    removeMeals,
    batchOperations,
    editMealBatches,
    getDefaultAddSwapSettings,
    profile,
    user,
    mealType,
    offset,
    startReplaceMeal,
    plan = null
}) {
    // State
    const [loading, setLoading] = useState(false);
    const [canUndoRefresh, setCanUndoRefresh] = useState(false);
    const [cachedMeals, setCachedMeals] = useState(null);
    const [lastRefreshedMeals, setLastRefreshedMeals] = useState(null);
    const [usedRecipeUuids, setUsedRecipeUuids] = useState([]);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    // Refs
    const intervalRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Build comprehensive search parameters using Smart Choices nutrition intelligence
    const buildSearchParams = useCallback((meals, profile, participants, mealType, envelope, extraFilters) => {
        // Create add/swap parameter object that Smart Choices functions expect
        const addSwapParams = {
            plan: plan ? plan : { meals: [] }, // Empty plan for base calculation
            mealType,
            replaceMeals: meals,
            meals: [], // All meals for the day (for envelope calculation)
            contents: {}, // Will be populated with current recipes/foods if needed
            profile,
            modalSettings: {
                extraFilters: extraFilters || {},
                ideals: {},
                envelope: envelope || {}
            },
            excludeUuids: [],
        };

        // Apply Smart Choices nutrition intelligence
        addDefaultAddSwapTags(addSwapParams);
        addDefaultAddSwapFilters(addSwapParams);

        // Build base parameters structure
        let params = {
            language: profile.language || 'en',
            types: ['recipe', 'combo'],
            filters: {},
            size: 1,
            sort_by: 'ideal_smart_choices',
            sort_params: {},
            include_library: true,
        };

        // Copy filters from Smart Choices logic
        if (addSwapParams.modalSettings.tags) {
            params.filters.tags = addSwapParams.modalSettings.tags;
        }

        if (addSwapParams.modalSettings.excludeTags) {
            params.filters['!tags'] = addSwapParams.modalSettings.excludeTags;
        }

        if (addSwapParams.modalSettings.avoidIngredients) {
            params.filters['!ingredient_tags'] = addSwapParams.modalSettings.avoidIngredients;
        }

        // Apply any extra filters
        Object.assign(params.filters, extraFilters || {});

        // Build sort_params with comprehensive nutrition intelligence
        params.sort_params = {
            ...params.sort_params,
            envelope: envelope || {},
            ideals: addSwapParams.modalSettings.ideals || {},
            conditions: (profile.conditions || []).map(cd => cd.name),
            avoidances: addSwapParams.modalSettings.avoidIngredients || []
        };

        // Add user preferences if available
        if (profile.preferences) {
            if (profile.preferences.cuisines && profile.preferences.cuisines.length > 0) {
                params.sort_params.prefers = profile.preferences.cuisines;
            }
        }

        return params;
    }, []);

    // Search function using /search-uniques endpoint
    const search = useCallback(async (params, participants) => {
        const url = '/search-uniques';

        setLoading(true);

        try {
            const response = await AuthStore.fetch(getConfig('recipe_api') + url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
                body: JSON.stringify(params),
            }, true);

            // Ensure we always return a consistent structure
            if (!response) {
                console.warn('Empty response from search API');
                return { results: null };
            }

            return { results: response };
        } catch (error) {
            console.error('Search error:', error);
            return { results: null };
        }
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback((params, participants) => {
        return new Promise((resolve) => {
            // Clear any existing timeout
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            // Set new timeout
            searchTimeoutRef.current = setTimeout(async () => {
                const result = await search(params, participants);
                resolve(result);
            }, 350);
        });
    }, [search]);

    // Initialize used recipe UUIDs from current meals
    useEffect(() => {
        if (meals?.length && !usedRecipeUuids.length) {
            const { recipe } = getPrimaryMeal(meals, recipes, foods);
            if (recipe) {
                setUsedRecipeUuids([recipe.uuid]);
            }
        }
    }, [meals, recipes, foods, usedRecipeUuids.length]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Set up undo timer
    const setUndoRefresh = useCallback((timeleft) => {
        intervalRef.current = setInterval(() => {
            if (timeleft <= 0) {
                setCanUndoRefresh(false);
                setCachedMeals(lastRefreshedMeals);
                clearInterval(intervalRef.current);
            }
            timeleft -= 1;
        }, 1000);
    }, [lastRefreshedMeals]);

    // Undo refresh action
    const undoRefresh = useCallback(() => {
        clearInterval(intervalRef.current);
        setCanUndoRefresh(false);

        if (!cachedMeals || !lastRefreshedMeals) return;

        // Calculate meals to delete (new meals that weren't in cached)
        const mealsToDelete = (meals || []).filter(meal =>
            !cachedMeals.find(cachedMeal => cachedMeal.id === meal.id)
        );

        // Restore cached meals and remove meals that were added during refresh
        if (cachedMeals.length > 0) {
            addMeals(cachedMeals);
        }
        if (mealsToDelete.length > 0) {
            removeMeals(mealsToDelete.map(meal => meal.uuid || meal.id));
        }
        setCachedMeals(null);
        setLastRefreshedMeals(null);
    }, [cachedMeals, lastRefreshedMeals, meals, addMeals, removeMeals]);

    // Main refresh function with enhanced error handling
    const refreshMeal = useCallback(async () => {
        if (!meals?.length || !profile || !user) {
            setError('Unable to refresh meal: missing required data');
            return;
        }

        // Clear any previous errors
        setError(null);

        // Get meal type info
        const mealTypeInfo = getMealTypeInfoByName(mealType, null, plan?.meal_types || profile?.preferences?.meal_types);
        const envelope = mealTypeInfo?.envelope;

        // Get participants
        const participants = getParticipantsForProfileByMealType(profile, mealType);

        // Get default settings
        const settings = getDefaultAddSwapSettings ?
            getDefaultAddSwapSettings(participants, offset, mealTypeInfo, meals) :
            {};

        const { defaultAvoids, defaultExcludes, defaultTags, extraFilters, ideals } = settings;

        // Build extra filters for exclusions
        const exclusionFilters = {
            "!uuid": usedRecipeUuids,
            "!main_dish": usedRecipeUuids,
            "!side_dish": usedRecipeUuids,
            "tags": [mealTypeInfo.main_dish]
        };

        // Build comprehensive search params with Smart Choices nutrition intelligence
        const params = buildSearchParams(meals, profile, participants, mealTypeInfo, envelope, exclusionFilters);

        // Execute search with comprehensive error handling
        let searchResult;
        try {
            searchResult = await debouncedSearch(params, participants);
        } catch (error) {
            console.error('Search execution error:', error);
            setError(`Failed to search for replacement meal: ${error.message || 'Network error'}`);
            setLoading(false);

            // Retry logic for network errors
            if (retryCount < 2 && (error.message?.includes('network') || error.message?.includes('timeout'))) {
                setTimeout(() => {
                    setRetryCount(prev => prev + 1);
                    refreshMeal();
                }, 1000 * (retryCount + 1)); // Exponential backoff
            }
            return;
        }

        if (!searchResult || !searchResult.results) {
            console.warn('No search results returned', searchResult);
            setError('No replacement meals found. Please try again or use Smart Choices for more options.');
            setLoading(false);

            // Reset retry count on successful response but no results
            setRetryCount(0);
            return;
        }

        const results = searchResult.results;

        // Handle results
        if (results.elements && results.elements.length > 0) {
            const result = results.elements[0];

            const mealsToCache = cachedMeals || meals.filter(meal => !meal.deleted);
            setCachedMeals(mealsToCache);
            setCanUndoRefresh(true);

            const { primary: oldPrimary } = getPrimaryMeal(meals, recipes, foods);

            if (result.type === 'combo') {
                // Handle combo meals
                setError('Combo meal refresh is not yet supported. Please use Smart Choices to replace this meal.');
                setLoading(false);

                // Fallback to Smart Choices for combo meals
                setTimeout(() => {
                    const { primary } = getPrimaryMeal(meals, recipes, foods);
                    startReplaceMeal(primary.offset, primary.meal, meals, plan);
                }, 100);
                return;
            } else {
                // Handle single recipe
                if (!result.servings) {
                    console.error('Recipe missing servings:', result);
                    setError('Replacement recipe is missing serving information. Please try again.');
                    setLoading(false);
                    return;
                }
                // Always get servings from the recipe object
                const oldRecipe = recipes[oldPrimary.recipe_uuid];

                const primaryServings = oldRecipe?.servings || 1;
                const oldScaling = oldPrimary.scaling || 1;


                const scaling = Math.ceil((oldScaling * primaryServings) / result.servings);
                const side_dish = ['Breakfast Side Dish', 'Lunch Side Dish', 'Side Dish'].some(t => (result.tags || []).includes(t));


                const meal = {
                    id: uuidGen().substring(0, 8),
                    meal: mealType,
                    offset: offset,
                    meal_type: 'fresh',
                    recipe_uuid: result.uuid,
                    details_uuid: result.details,
                    recipe_title: result.title,
                    recipe_image: result.image,
                    scaling: isNaN(scaling) ? 1 : scaling, // Default to 1 if NaN
                    participants: participants && participants.length > 0 ? participants.map(p => p.uuid || '') : [],
                    side_dish,
                    meal_batch_id: uuidGen(),
                };


                // Match old behavior: create new meals and replace old ones
                // The second parameter should be the meals to delete/replace

                // Remove old meals and add new refreshed meal atomically
                batchOperations((batchOps) => {
                    if (meals.length > 0) {
                        meals.forEach((mealToRemove) => {
                            batchOps.removeMeal(mealToRemove.uuid || mealToRemove.id);
                        });
                    }
                    batchOps.addMeals([meal]);
                });
                setUsedRecipeUuids(prev => [...prev, result.uuid]);
                setLastRefreshedMeals([meal]);
            }

            // Success feedback and cleanup
            Analytics.refreshMeal();
            clearInterval(intervalRef.current);
            setUndoRefresh(5);
            setRetryCount(0); // Reset retry count on success
            setError(null); // Clear any previous errors

        } else if (usedRecipeUuids.length > 1) {
            // Reset and retry
            setUsedRecipeUuids([usedRecipeUuids[usedRecipeUuids.length - 1]]);
            setTimeout(() => refreshMeal(), 0);
        } else {
            // No results on first attempt - provide user feedback and fallback to Smart Choices
            setError('No suitable replacement meals found. Opening Smart Choices for more options...');
            setLoading(false);

            const { primary } = getPrimaryMeal(meals, recipes, foods);
            startReplaceMeal(primary.offset, primary.meal, meals, plan);
            Analytics.refreshMealFallback();

            // Clear error after opening Smart Choices
            setTimeout(() => setError(null), 3000);
        }

        setLoading(false);
    }, [meals, recipes, foods, usedRecipeUuids, getDefaultAddSwapSettings,
        addMeals, removeMeals, batchOperations, editMealBatches, setUndoRefresh, profile, user, mealType,
        offset, buildSearchParams, startReplaceMeal, debouncedSearch, retryCount]);

    // Reset used UUIDs when meals change significantly
    const resetUsedUuids = useCallback(() => {
        if (meals?.length) {
            const { recipe } = getPrimaryMeal(meals, recipes, foods);
            setUsedRecipeUuids(recipe ? [recipe.uuid] : []);
        }
    }, [meals, recipes, foods]);

    // Clear error function
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // State
        loading,
        canUndoRefresh,
        error,
        retryCount,

        // Actions
        refreshMeal,
        undoRefresh,
        resetUsedUuids,
        clearError,

        // For external state management if needed
        usedRecipeUuids,
        setUsedRecipeUuids
    };
}

export default useMealRefresh;
